import { useEffect, useState } from 'react';

import {
  Notifications as NotificationsIcon,
  Assignment as TaskIcon,
  CheckCircle as CheckCircleIcon,
  Domain as DomainIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  PlayCircle as StartedIcon,
  RadioButtonUnchecked as PendingIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Build as BuildIcon,
  CleaningServices as CleaningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';

import PageHeader from '../../components/PageHeader';
import {
  ACTIVITY_CLUSTERS,
  ClusterName,
  TASK_SEQUENCE,
  TaskStatus,
  useNotifications,
} from '../../contexts/NotificationsContext';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const TaskChip = styled(Chip)<{ taskStatus?: TaskStatus }>(({ theme, taskStatus }) => {
  const getStatusColor = () => {
    switch (taskStatus) {
      case 'completed':
      case 'finished':
        return {
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.main,
        };
      case 'failed':
        return {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.dark,
          borderColor: theme.palette.error.main,
        };
      case 'started':
        return {
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.dark,
          borderColor: theme.palette.info.main,
        };
      default:
        return {
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
          borderColor: theme.palette.grey[400],
        };
    }
  };

  return {
    ...getStatusColor(),
    borderWidth: 1,
    borderStyle: 'solid',
    fontWeight: 500,
    margin: theme.spacing(0.5),
  };
});

const ExpandMoreButton = styled(IconButton)<{ expand: boolean }>(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  padding: theme.spacing(2),
  borderRadius: '12px 12px 0 0',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 36,
  marginBottom: theme.spacing(2),
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 36,
  textTransform: 'none',
  fontWeight: 500,
  marginRight: theme.spacing(2),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

// Helper function to get task status icon
const getTaskStatusIcon = (status?: TaskStatus) => {
  switch (status) {
    case 'completed':
    case 'finished':
      return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    case 'failed':
      return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    case 'started':
      return <StartedIcon sx={{ fontSize: 16, color: 'info.main' }} />;
    default:
      return <PendingIcon sx={{ fontSize: 16, color: 'grey.500' }} />;
  }
};

// Helper function to get cluster icon
const getClusterIcon = (clusterName: ClusterName) => {
  switch (clusterName) {
    case 'Crawling':
      return <StorageIcon sx={{ fontSize: 18, mr: 1 }} />;
    case 'DA Preparation':
      return <BuildIcon sx={{ fontSize: 18, mr: 1 }} />;
    case 'Housekeeping':
      return <CleaningIcon sx={{ fontSize: 18, mr: 1 }} />;
    default:
      return <TaskIcon sx={{ fontSize: 18, mr: 1 }} />;
  }
};

// Helper function to format domain
const formatDomain = (domain: string) => {
  return domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
};

// Helper function to format task name
const formatTaskName = (taskName: string) => {
  return taskName
    .replace(/_/g, ' ')
    .replace(/task/g, '')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format time ago
const formatTimeAgo = (date: Date) => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
};

const CompanyProgressCard = ({ domain, progress }: { domain: string; progress: any }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeCluster, setActiveCluster] = useState<ClusterName>('Crawling');
  const theme = useTheme();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Get task statuses grouped by cluster
  const getClusterTaskStatuses = (clusterName: ClusterName) => {
    const clusterTasks = ACTIVITY_CLUSTERS[clusterName];
    return [...clusterTasks] // Create a mutable copy of the readonly array
      .sort((a, b) => a.sequence - b.sequence) // Sort by sequence number
      .map(taskDef => {
        const task = progress.tasks.get(taskDef.task);
        return {
          name: taskDef.task,
          sequence: taskDef.sequence,
          status: task?.status || 'pending',
          timestamp: task?.timestamp,
        };
      });
  };

  // Get overall task statuses (for summary)
  const taskStatuses = TASK_SEQUENCE.map(taskName => {
    const task = progress.tasks.get(taskName);
    return {
      name: taskName,
      status: task?.status || 'pending',
      timestamp: task?.timestamp,
    };
  });

  const completedCount = taskStatuses.filter(t => t.status === 'completed' || t.status === 'finished').length;
  const failedCount = taskStatuses.filter(t => t.status === 'failed').length;
  const inProgressCount = taskStatuses.filter(t => t.status === 'started').length;
  const pendingCount = TASK_SEQUENCE.length - completedCount - failedCount - inProgressCount;

  return (
    <StyledCard>
      <HeaderBox>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <DomainIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600} color="primary.dark">
                {formatDomain(domain)}
              </Typography>
              {progress.companyId && (
                <Typography variant="caption" color="text.secondary">
                  Company ID: {progress.companyId}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatTimeAgo(progress.lastUpdate)}
              </Typography>
            </Box>
            <ExpandMoreButton
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMoreButton>
          </Box>
        </Box>
      </HeaderBox>

      <CardContent>
        <Grid2 container spacing={2} alignItems="center">
          <Grid2 size={{ xs: 12, md: 8 }}>
            <ProgressContainer>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight={500}>
                  Progress: {completedCount}/{TASK_SEQUENCE.length} tasks
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {Math.round(progress.overallProgress)}%
                </Typography>
              </Box>
              <StyledLinearProgress variant="determinate" value={progress.overallProgress} />
            </ProgressContainer>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
              {completedCount > 0 && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={`${completedCount} Complete`}
                  color="success"
                  variant="outlined"
                />
              )}
              {inProgressCount > 0 && (
                <Chip
                  size="small"
                  icon={<StartedIcon />}
                  label={`${inProgressCount} Running`}
                  color="info"
                  variant="outlined"
                />
              )}
              {pendingCount > 0 && (
                <Chip size="small" icon={<PendingIcon />} label={`${pendingCount} Pending`} variant="outlined" />
              )}
              {failedCount > 0 && (
                <Chip
                  size="small"
                  icon={<ErrorIcon />}
                  label={`${failedCount} Failed`}
                  color="error"
                  variant="outlined"
                />
              )}
            </Stack>
          </Grid2>
        </Grid2>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            <TaskIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1 }} />
            Activity Clusters
          </Typography>

          <StyledTabs value={activeCluster} onChange={(_, value) => setActiveCluster(value as ClusterName)}>
            {Object.keys(ACTIVITY_CLUSTERS).map(clusterName => {
              const cluster = clusterName as ClusterName;
              const clusterTasks = getClusterTaskStatuses(cluster);
              const clusterCompleted = clusterTasks.filter(
                t => t.status === 'completed' || t.status === 'finished'
              ).length;
              const clusterTotal = clusterTasks.length;
              const hasActivity = clusterTasks.some(t => t.status !== 'pending');

              return (
                <StyledTab
                  key={cluster}
                  value={cluster}
                  label={
                    <Box display="flex" alignItems="center">
                      {getClusterIcon(cluster)}
                      <span>{cluster}</span>
                      {clusterTotal > 0 && (
                        <Chip
                          size="small"
                          label={`${clusterCompleted}/${clusterTotal}`}
                          sx={{
                            ml: 1,
                            height: 20,
                            backgroundColor: hasActivity
                              ? clusterCompleted === clusterTotal
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.info.main, 0.1)
                              : alpha(theme.palette.grey[400], 0.1),
                          }}
                        />
                      )}
                    </Box>
                  }
                  disabled={clusterTotal === 0}
                />
              );
            })}
          </StyledTabs>

          <Box sx={{ mt: 2 }}>
            <Grid2 container spacing={1}>
              {getClusterTaskStatuses(activeCluster).map((task, index) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={task.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: alpha(theme.palette.grey[100], 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTaskStatusIcon(task.status)}
                      <Box flex={1}>
                        <Typography variant="caption" fontWeight={500} display="block">
                          {task.sequence}. {formatTaskName(task.name)}
                        </Typography>
                        <TaskChip label={task.status} size="small" taskStatus={task.status} />
                        {task.timestamp && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatTimeAgo(task.timestamp)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Collapse>
      </CardContent>
    </StyledCard>
  );
};

const NotificationsPage = () => {
  const { companyProgressMap, markAllAsRead, addNotification } = useNotifications();
  const [pollingInterval, setPollingInterval] = useState(60); // Poll every 60 seconds

  useEffect(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  // Poll for DynamoDB events
  useEffect(() => {
    const fetchDynamoDBEvents = async () => {
      try {
        // This would use the GraphQL query once backend is running
        // For now, we'll comment it out to prevent errors
        /*
        const response = await client.query({
          query: GET_EVENTS_OF_LAST_MINS,
          variables: { noOfMinutes: 5 },
        });
        
        const events = response.data?.getEventsOfLastMins || [];
        events.forEach((event: any) => {
          addNotification({
            message: `(status: '${event.statusMessage}', task_name: '${event.taskName}', company_domain: '${event.companyDomain}', company_id: ${event.companyId})`,
            timestamp: new Date(event.timestamp * 1000),
            read: false,
            companyDomain: event.companyDomain,
            companyId: event.companyId,
            taskName: event.taskName,
            taskStatus: event.statusMessage as any,
          });
        });
        */
      } catch (error) {
        console.error('Error fetching DynamoDB events:', error);
      }
    };

    // Initial fetch
    fetchDynamoDBEvents();

    // Set up polling
    const intervalId = setInterval(fetchDynamoDBEvents, pollingInterval * 1000);

    return () => clearInterval(intervalId);
  }, [pollingInterval, addNotification]);

  // Sort companies by last update time
  const sortedCompanies = Array.from(companyProgressMap.entries()).sort(
    ([, a], [, b]) => b.lastUpdate.getTime() - a.lastUpdate.getTime()
  );

  if (sortedCompanies.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="text.secondary">
          No crawl activities yet
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Company crawl progress will appear here when tasks are running
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Notifications" subtitle="Monitor crawl progress and system notifications" variant="default" />
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Track real-time progress of company data collection and analysis tasks
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box>
          {sortedCompanies.map(([domain, progress]) => (
            <CompanyProgressCard key={domain} domain={domain} progress={progress} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationsPage;
